import React, { useMemo, useRef, useState } from 'react';
import { getDrawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import {
  createFacultativeMutation,
  GetActiveProposalsPaginated,
  getPolicyDetailsQuery,
  getPolicyListQuery,
  getProposalDetailsQuery,
} from './queries';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { extractDetails } from './utils';
import { useNavigate } from 'react-router-dom';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { IFacultativeDrawerLovs, IFacultativeDrawerValues } from '.';
import { getReinsurersListQuery } from '../shared/queries';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { valueOrNull } from '@utils/helper-utils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';

interface INewFacultativeDrawer {
  isOpen: boolean;
  onClose: () => void;
}

const initialValues: IFacultativeDrawerValues = {
  linkedTo: 'proposal',
  policy: '',
  proposal: '',
  company: '',
  currencySymbol: '',
  currencyCode: '',
  cover: '',
  coverEffectiveDate: '',
  coverExpiryDate: '',
  reinsurer: '',
};

const NewFacultativeDrawer: React.FC<INewFacultativeDrawer> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [getProposalListLazy, { loading: activeProposalsLoading }] =
    useLazyQuery(GetActiveProposalsPaginated);
  const [getPolicyListLazy, { loading: policyListLoading }] =
    useLazyQuery(getPolicyListQuery);
  const [getReinsurersListLazy, { loading: reinsurersLoading }] = useLazyQuery(
    getReinsurersListQuery
  );
  const [getProposalDetailsLazy, { loading: proposalDetailsLoading }] =
    useLazyQuery(getProposalDetailsQuery);
  const [getPolicyDetailsLazy, { loading: policyDetailsLoading }] =
    useLazyQuery(getPolicyDetailsQuery);

  const [createFacultative, { loading }] = useMutation(
    createFacultativeMutation
  );
  const [lovs, setLovs] = useState<IFacultativeDrawerLovs>({
    proposals: {},
    reinsurers: {
      options: {},
      totalCount: 0,
    },
    covers: {
      name: {},
      dates: {},
    },
  });
  const companyValue = useRef<string>();
  const [values, setValues] = useState<IFacultativeDrawerValues>(initialValues);

  const formInputs = useMemo(
    () => getDrawerSections(lovs, values),
    [lovs, values]
  );

  const fetchDetails = async (
    type: 'proposal' | 'policy',
    value: string,
    values: IFacultativeDrawerValues
  ) => {
    try {
      const [detailsResponse, reinsurersResponse] = await Promise.all([
        type === 'proposal'
          ? getProposalDetailsLazy({
              variables: {
                proposalId: value,
              },
            })
          : getPolicyDetailsLazy({
              variables: {
                policyID: value,
              },
            }),
        getReinsurersListLazy({
          variables: {
            SelectedCompany: value,
            KeywordSearch: null,
            pagination: {
              pageSize: 10,
              pageNumber: 1,
            },
          },
        }),
      ]);

      const { data: detailsData, error: detailsError } = detailsResponse;
      const { data: reinsurersData, error: reinsurersError } =
        reinsurersResponse;

      const { company, currencyCode, currencySymbol, covers } = extractDetails(
        detailsData,
        type
      );

      const reinsurers = {
        options: lookupListAsRecordObject(
          reinsurersData?.Production?.queries?.ReinsurersList?.items,
          false,
          'salesforceManagement_BusinessPartner_Id',
          'salesforceManagement_BusinessPartner_FullName'
        ),
        totalCount:
          reinsurersData?.Production?.queries?.getProposalList?.paging
            ?.totalCount,
      };

      setLovs((prev) => ({
        ...prev,
        covers,
        reinsurers,
      }));

      values.currencyCode = currencyCode;
      values.currencySymbol = currencySymbol;

      if (detailsError) {
        toast.error(
          <ToastErrorMessage>{getError(detailsError)}</ToastErrorMessage>
        );
        companyValue.current = '';
        values.currencyCode = '';
        values.currencySymbol = '';
      } else {
        companyValue.current = company;
      }

      if (reinsurersError) {
        toast.error(
          <ToastErrorMessage>{getError(reinsurersError)}</ToastErrorMessage>
        );
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const handleChange = async (
    fieldName: keyof IFacultativeDrawerValues,
    value: string,
    sectionIndex: number,
    values: IFacultativeDrawerValues
  ) => {
    if (fieldName === 'linkedTo') {
      values.policy = '';
      values.proposal = '';
      companyValue.current = '';
      values.currencyCode = '';
      values.currencySymbol = '';
      values.cover = '';
      values.coverEffectiveDate = '';
      values.coverExpiryDate = '';
    } else if (
      fieldName === 'proposal' &&
      values.linkedTo === 'proposal' &&
      value
    ) {
      values.policy = '';
      values.cover = '';
      values.coverEffectiveDate = '';
      values.coverExpiryDate = '';
      await fetchDetails(fieldName, value, values);
    } else if (
      fieldName === 'policy' &&
      values.linkedTo === 'policy' &&
      value
    ) {
      values.proposal = '';
      values.cover = '';
      values.coverEffectiveDate = '';
      values.coverExpiryDate = '';
      await fetchDetails(fieldName, value, values);
    } else if (fieldName === 'cover' && value) {
      const cover = lovs.covers.dates[value];
      values.coverEffectiveDate = cover.effectiveDate;
      values.coverExpiryDate = cover.expiryDate;
    }

    setValues(values);
  };

  const handleSubmit = async () => {
    try {
      const { data, errors } = await createFacultative({
        variables: {
          Proposal: valueOrNull(values.proposal),
          Policy: valueOrNull(values.policy),
          LinkedTo: values.linkedTo.toUpperCase(),
          Company: companyValue.current,
          ProposalCover: values.cover,
          Reinsurer: values.reinsurer,
        },
      });

      if (!errors) {
        const createdFacId = data?.production?.actions?.createFacultative?.id;

        if (createdFacId) {
          toast.success(
            <ToastSuccessMessage>
              Facultative created successfully
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            navigate('/reinsurance/facultatives/' + createdFacId);
          }, 750);
        }
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Add New Facultative'}
      onClose={() => onClose()}
      isOpen={isOpen}
      disableForm={loading}
      isSubmitButtonDisabled={loading}
      sections={formInputs}
      onSubmit={handleSubmit}
      loadingFields={{
        proposal: activeProposalsLoading,
        reinsurer: reinsurersLoading,
        cover: proposalDetailsLoading || policyDetailsLoading,
        policy: policyListLoading,
      }}
      onChange={handleChange}
      onAutocompleteSearch={(fieldName: string, value: string, pagination) => {
        if (fieldName === 'reinsurer' && companyValue.current) {
          return getReinsurersListLazy({
            variables: {
              SelectedCompany: companyValue.current,
              KeywordSearch: value,
              pagination,
            },
          }).then((result) => {
            return {
              options: lookupListAsRecordObject(
                result.data?.Production?.queries?.ReinsurersList?.items,
                false,
                'salesforceManagement_BusinessPartner_Id',
                'salesforceManagement_BusinessPartner_FullName'
              ),
              totalCount:
                result.data?.Production?.queries?.getProposalList?.paging
                  ?.totalCount,
            };
          });
        } else if (fieldName === 'proposal') {
          return getProposalListLazy({
            variables: {
              KeywordSearch: value,
              pagination,
            },
          }).then((result) => {
            return {
              options: lookupListAsRecordObject(
                result.data.Production?.queries?.GetActiveProposalsPaginated
                  ?.items,
                false,
                'production_Proposal_Id',
                'production_Proposal_ProposalName'
              ),
              totalCount:
                result.data?.Production?.queries?.GetActiveProposalsPaginated
                  ?.paging?.totalCount,
            };
          });
        } else if (fieldName === 'policy') {
          return getPolicyListLazy({
            variables: {
              KeywordSearch: value,
              pagination,
            },
          }).then((result) => {
            return {
              options: lookupListAsRecordObject(
                result.data?.Production?.queries?.getLatestPolicyList?.items,
                false,
                'production_Policy_Id',
                'production_Policy_PolicyNumber'
              ),
              totalCount:
                result.data?.Production?.queries?.getLatestPolicyList?.paging
                  ?.totalCount,
            };
          });
        }

        return new Promise<{
          options: Record<string, string>;
          totalCount: number;
        }>((resolve) => {
          resolve({ options: {}, totalCount: 0 });
        });
      }}
    />
  );
};

export default NewFacultativeDrawer;
